import { PriceRange } from '@/modules/Service/types'
import {
    SLASource,
    SLARule,
    EscalationRule,
    CustomTerm,
    BusinessHoursConfig,
} from '@/modules/Service/types/sla'
import { Attachment } from '@/modules/Upload/types'
import { Dayjs } from 'dayjs'

// Service SLA payload for contract creation
export interface ContractServiceSLA {
    sla_template_id?: number
    sla_source: SLASource
    effective_from?: string | Dayjs | null
    effective_to?: string | Dayjs | null
    uptime_guarantee?: number
    downtime_allowance_minutes?: number
    business_hours_only?: boolean
    business_hours_config?: BusinessHoursConfig
    sla_rules?: SLARule[]
    escalation_rules?: EscalationRule[]
    custom_terms?: CustomTerm[]
}

// NDA attachment for contract creation
export interface NDAAttachment {
    file: File
    url?: string
    uploading?: boolean
}

export interface Contract {
    sla_enable: boolean
    nda_enable: boolean
    currency_code: string
    exchange_rate: number
    contract_role: ContractRole
    contract_direction: ContractDirection
    use_contract_id?: boolean
    contract_id?: number
    partner_id?: number | null
    contract_note: string
    contract_effective_date: Dayjs | null | string
    contract_expiry_date: Dayjs | null | string
    contract_value: number
    customer_id: number
    customer_journey_id: number
    customer_contact_id: number
    employee_sale_tracking_ids?: number[]
    service_sla?: ContractServiceSLA
    nda_attachments?: NDAAttachment[]
    order: {
        order_source: string
        order_source_name: string
        order_note: string
        order_amount: number
        products: Partial<Product>[]
    }
}

export interface GetProductByGroupNameResponse {
    product_id: number
    product_name: string
    template_id: string
    template_name: string
    product_hidden: boolean
}

export interface Product {
    product_id: number
    product_name: string
    product_group_code: string
    product_group_name: string
    product_form: string
    service_start_date: string
    service_stopped_at: string
    service_price_register: number
    service_times: ServiceTimes
    service_configoptions: ServiceConfigOption[]
    service_domain_metadata: Domain & {
        other_standard_payment_schedule?: OtherStandardPaymentSchedule
    }
    editable: boolean
    service_price_model?: string
    employee_sale_tracking_ids?: number[]
}

export interface OtherStandardPaymentRange {
    start_date: Dayjs | null | string
    end_date: Dayjs | null | string
    price: number
}

export interface OtherStandardPaymentSchedule {
    payment_method: 'prepaid' | 'postpaid'
    payment_mode: 'one_time' | 'multiple_times'
    description?: string
    ranges: OtherStandardPaymentRange[]
}

export interface ProductPrice {
    product_price_id: number
    product_id: number
    price: number
    start_date: string
    end_date: string
    currency: string
    created_at: string
    updated_at: string
}

export interface ServiceTimes {
    first_startdate: Dayjs | null | string
    first_enddate: Dayjs | null | string
    continue_startdate: Dayjs | null | string
    continue_enddate: Dayjs | null | string
    renew_range?: number // Days before which contract renewal should be initiated
    statement?: string
    statement_startdate: Dayjs | null | string
    statement_enddate: Dayjs | null | string
    current_startdate: Dayjs | null | string
    current_enddate: Dayjs | null | string
}

export interface ServiceConfigOption {
    name: string
    unit: string
    priceoff?: boolean
    resizable?: boolean
    quota?: number
    statementable?: boolean
    commitment_value?: number
    commitment_price_per_unit?: number
    commitment_price_total?: number
    commitment_startdate?: Dayjs | null | string
    commitment_enddate?: Dayjs | null | string
    commitment_auto_renewable?: boolean
    commitment_prepaid?: boolean
    commitment_fulltimetable?: boolean
    warning_enabled?: boolean
    warning_values?: WarningValue[]
    prices?: PriceRange[]
    product_form?: string
    commitment_time: number
    capacity_value?: number
    capacity_unit?: string
    control_unit_price_exceeds?: number
    brand_name?: string
    model_name?: string
    type?: string
    price_config?: {
        method: string
    }
}

export interface WarningValue {
    priority: string
    value: number
}

export interface Product {
    product_id: number
    product_name: string
    product_code: string
    product_hidden: boolean
    product_description: string
    product_group_id: number
    template_id: number
    created_at: string
    updated_at: string
    product_template: ProductTemplate
    product_config_options: ProductConfigOption[]
    product_prices: ProductPrice[]
    prepaid: boolean
}

export interface ProductTemplate {
    template_id: number
    template_name: string
    template_uuid: string
    created_at: string
    updated_at: string
    deleted_at: any
}

export interface ProductConfigOption {
    max: number
    min: number
    name: string
    options: any[]
    price: number
    product_config_option_id: number
    subtitle: string
    title: string
    type: string
    unit: string
    value: number
    warehouse: any
    product_pricing_steps: ProductPricingStep[]
    is_editable: boolean
    statementable?: boolean
}

export interface ProductPricingStep {
    price_end: number
    price_per_unit: number
    price_start: number
    product_pricing_step_id?: number
}

export interface SingleProductConfigOptionProps {
    index: number
    name: string
    productIndex: number
    remove: (index: number) => void
}

export interface Model {
    warehouse_model_id: number
    warehouse_model_name: string
    manufacture_id: number
    slots: any[]
    specifications: Specification[]
    warehouse_type: WarehouseType
    manufacture: Manufacture
}

export interface Specification {
    specification_name: string
    specification_unit: string
    specification_value: string
    warehouse_model_id: number
    specification_id: number
}

export interface WarehouseType {
    warehouse_type_id: number
    warehouse_type_name: string
    warehouse_type_parent: number
    warehouse_type_unit: string
    warehouse_group_id: number
}

export interface Manufacture {
    manufacture_id: number
    manufacture_name: string
    manufacture_address: string
    manufacture_created_at: string
    manufacture_updated_at: string
}

export interface Domain {
    domain_type: string
    domain_value: string
    domain_registrationperiod: string
    domain_eppcode: string
    dns_option: string
    addons: Addon[]
    domain_extra_fields: DomainExtraFields
}

export interface Addon {
    name: string
    label: string
    active: boolean
}

export interface DomainExtraFields {
    dns: Dn[]
    for: string
    ownerName: string
    ownerTaxCode: string
    ownerID_Number: string
    ownerAddress: string
    ownerWard: string
    ownerDistrict: string
    ownerProvince: string
    ownerCountry: string
    ownerEmail1: string
    ownerEmail2: string
    ownerPhone: string
    ownerFax: string
    uiName: string
    uiTaxCode: string
    uiID_Number: string
    uiAddress: string
    uiWard: string
    uiDistrict: string
    uiProvince: string
    uiCountry: string
    uiEmail: string
    uiPhone: string
    uiGender: string
    uiFax: string
    uiBirthdate: string
    uiCompany: string
    uiPosition: string
    adminName: string
    adminID_Number: string
    adminAddress: string
    adminWard: string
    adminDistrict: string
    adminProvince: string
    adminCountry: string
    adminEmail: string
    adminPhone: string
    adminGender: string
    adminFax: string
    adminBirthdate: string
    adminPosition: string
    sendmail: boolean
}

export interface Dn {
    name: string
}

export interface DomainPrice {
    domain_price_id: number
    dp_extension: string
    dp_dnsmanagement: boolean
    dp_email_forwarding: boolean
    dp_ip_protection: boolean
    dp_eppcode: string
    dp_autoreg: string
    dp_group: any
    dp_grace_period: number
    dp_grace_period_fee: number
    dp_redemption_grace_period: number
    dp_redemption_grace_period_fee: number
    dp_item_created_at: string
    dp_item_updated_at: string
    dp_extra_fields: string
    dp_isvn: boolean
    dp_price_register: number
    dp_price_renew: number
    dp_price_transfer: number
}

export enum ContractStatus {
    Active = 'active',
    Uploading = 'uploading',
    Pending = 'pending',
    Exchanging = 'exchanging',
    Outdate = 'outdate',
    Retained = 'retainted',
} // Thêm các trạng thái khác nếu có
type ContractType = 'customer' | 'supplier' // Thêm các loại khác nếu có

export type CustomerContract = {
    contract_id: number
    contract_key: string | null
    contract_number_id: string
    contract_note: string | null
    contract_status: ContractStatus
    contract_type: ContractType
    contract_effective_date: string // "YYYY-MM-DD"
    contract_expiry_date: string | null
    contract_value: number
    contract_hidden: boolean
    contract_products: Record<string, any> | null // jsonb → object hoặc null
    employee_id: number | null
    customer_id: number | null
    service_id: number | null
    customer_contact_id: number | null
    customer_contact_name: string | null
    created_at: string // "YYYY-MM-DDTHH:mm:ss.SSSSSSZ"
    updated_at: string
    deleted_at: string | null
    customer_name: string
    company_name: string
    employee_name: string
    service_name: string
    attachments: Attachment[]
    sla_enable: boolean
    nda_enable: boolean
    auto_renewable: boolean
    is_renewable: boolean
    renewable_count: number
    first_contract_effective_date: string
    first_contract_expiry_date: string
    customer_industry?: string
    company_industry?: string
    product_group_name: string
    customer_position: string
    services: ContractService[]
    registration_date: string | null
    customer_language?: string
    partner_id?: number | null
    partner_name?: string | null
    partner_code?: string | null
    partner_email?: string | null
    partner_phone?: string | null
    contract_role?: ContractRole | null
    contract_direction?: ContractDirection | null
}

//
/**
 * One legal link between a service and a customer contract, as returned by
 * GET /services/:serviceID/contract.
 *
 * A service reaches a contract either directly (link_type 'contract') or through an annex /
 * phụ lục (link_type 'subcontract'). For an annex link the contract_* status and date fields
 * carry the ANNEX's own terms, while contract_number_id stays the parent contract's number.
 */
export type ServiceContractLink = {
    contract_id: number
    contract_number_id: string
    contract_status: ContractStatus
    contract_effective_date: string | null
    contract_expiry_date: string | null
    link_type: 'contract' | 'subcontract'
    subcontract_id: number | null
    subcontract_code: string | null
}

export interface ContractService {
    contract_value: number
    service_name: string
    service_id: number
    service_status: string
    start_date: string
    end_date: string
    product_group_name: string
}

export interface UpdateCustomerContractParams {
    sla_enable: boolean
    nda_enable: boolean
    contract_number_id: string | null
    contract_key: string | null
    contract_status?: string
    customer_id: number | null
    contract_note: string
    contract_value: number | null
    contract_effective_date: string | null
    contract_expiry_date: string | null
    partner_id?: number | null
    contract_role?: ContractRole | null
    contract_direction?: ContractDirection | null
}

export type ContractRole =
    | 'customer'
    | 'supplier'
    | 'reseller'
    | 'affiliate'
    | 'collaborator'

export type ContractDirection = 'earn' | 'pay'

export interface ContractStatistical {
    total_count: number
    created_count: number
    exchanging_count: number
    uploading_count: number
    active_count: number
    expired_count: number
    delta_total: number
    delta_created: number
    delta_exchanging: number
    delta_uploading: number
    delta_active: number
    delta_expired: number
    delta_active_percent: number
    delta_created_percent: number
    delta_exchanging_percent: number
    delta_expired_percent: number
    delta_uploading_percent: number
    delta_total_percent: number
}

export interface ContractOverviewTypes {
    contract_new: number
    contract_exchanging: number
    contract_uploading: number
    contract_expiried: number
    contract_completed: number
    contract_summaries: number
}

export interface ProductResponse {
    product_price_model: string
    product_id: number
    product_name: string
    product_code: string
    product_hidden: boolean
    product_description: string
    product_group_id: number
    template_id: number
    created_at: string
    updated_at: string
    is_product_addon: boolean
    template_name: string
    product_group_name: string
    product_group_type: any
    product_group_code: string
    product_price: number
    product_config_options: ProductConfigOption[]
}
